import { Link } from '@inertiajs/react'
import {
  CO_REBATE_BASE,
  CO_REBATE_CARGO_BONUS,
  CO_TAX_CREDIT,
} from '@/lib/constants'
import { resolveRebateWindow, formatWindowDate } from '@/lib/rebateWindow'

/**
 * Site-wide announcement bar for Colorado's e-bike rebate registration windows.
 *
 * The copy is derived from CO_REBATE_WINDOWS at render time rather than typed by
 * hand, so it cannot go stale: it counts down before a window opens, switches to
 * "closes <date>" while one is open, and disappears entirely once every published
 * window has passed (which is the cue to add the next year's dates to the
 * constant). Nobody has to remember to take it down.
 *
 * Rendered above the Header in Layout so it appears on every page — the state's
 * rebate announcement drives search and social traffic to arbitrary pages, not
 * just the homepage.
 *
 * SOURCE for all amounts and dates: https://energyoffice.colorado.gov/ebike-rebates
 * (verified 2026-08-06).
 */
export function RebateBanner() {
  const window = resolveRebateWindow()

  if (window.status === 'none') return null

  const isOpen = window.status === 'open'
  const maxOff = (CO_REBATE_BASE + CO_REBATE_CARGO_BONUS + CO_TAX_CREDIT).toLocaleString()

  const timing = isOpen
    ? `Registration is open through ${formatWindowDate(window.closes)}`
    : window.daysUntil <= 45
      ? `Registration opens ${formatWindowDate(window.opens)}`
      : null

  // More than ~6 weeks out, a countdown is noise — stay quiet until it's near.
  if (!timing) return null

  return (
    <div
      data-testid="rebate-banner"
      className="bg-primary text-background"
      role="region"
      aria-label="Colorado e-bike rebate announcement"
    >
      <div className="container flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2.5 text-center">
        {isOpen && (
          <span
            aria-hidden="true"
            className="relative inline-block h-2 w-2 shrink-0"
          >
            <span className="absolute inset-0 animate-ping rounded-full bg-secondary opacity-75" />
            <span className="absolute inset-0 rounded-full bg-secondary" />
          </span>
        )}

        <span className="text-sm font-semibold">
          Income-qualified Colorado cargo-bike rebate: up to ${maxOff} with the state credit.
        </span>

        <span className="text-sm text-[rgba(255,248,231,0.85)]">{timing}.</span>

        <Link
          href="/savings#rebate"
          className="text-sm font-semibold text-secondary underline underline-offset-2 hover:opacity-80"
        >
          How it works →
        </Link>
      </div>
    </div>
  )
}
